import { type RefObject, useEffect, useRef } from 'react';

/**
 * Mirror `value` into a ref via a passive effect, for callbacks that must read
 * the value as of *now* rather than as of the render that created them.
 *
 * The case this exists for is an **async continuation**: a callback that awaits,
 * then reads state after resuming. A fresh callback identity does not help there
 * — the code after the `await` closes over the value from the invocation that
 * started it, so by the time it resumes that value may be stale. Reading
 * `ref.current` after the await sees the latest committed value instead.
 *
 * `useEffectEvent` is the React 19 answer for stale closures, but it cannot be
 * passed to another hook, so a commit-phase ref sync remains the option when the
 * callback is handed to one (e.g. `useFilePicker`).
 *
 * Only safe when the ref is read from code that runs *after* React has committed
 * (event handlers, effects, async continuations). A native listener that runs
 * outside React's own event dispatch needs a `useLayoutEffect` sync instead —
 * this hook's passive `useEffect` has not run yet at that point.
 *
 * @example
 * ```typescript
 * const valueRef = useLatestRef(value);
 *
 * const onFileSelected = useCallback(async (file: File) => {
 *   const minted = await upload(file);
 *   // `value` here would be stale; `valueRef.current` is not.
 *   onChange([...(valueRef.current ?? []), minted.id]);
 * }, [onChange, valueRef]);
 * ```
 *
 * @template T - The type of the mirrored value
 */
export function useLatestRef<T>(value: T): RefObject<T> {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}
